// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// ── Cost schedule ────────────────────────────────────────────────────────────
//
// When does each dollar of the estimate get spent?
//
// The previous model gave a subcategory exactly one start and one end and
// spread its cost evenly between them. Work that genuinely happens in two
// stretches — concrete poured in two lifts four months apart — had to be
// modelled as one long continuous bar, which put money in the months between
// the pours where nothing is actually being spent. Widening the bar to cover
// both made the projection worse, not better.
//
// So a schedule entry here is a list of BANDS. Each band is a window with a
// share of the node's cost, and cost is spread evenly inside a band but not
// across the gaps between bands.
//
// Entries hang off nodes of the estimate tree and resolve by ONE rule:
//
//     the most specific schedule wins
//
// For a given line item, look for an entry on the item, then its subcategory,
// then its division. Whatever is found first governs that item's cost. So a
// user can schedule a whole division and stop, or open one subcategory and
// schedule it, and the rest keeps following the parent — nothing has to be
// scheduled twice and nothing is orphaned.
//
// Parents are drawn and totalled from what is actually beneath them, never
// from their own entry alone -- see rollupSpan and spreadLeaves. A division
// bar that ignored a line item scheduled outside its window would contradict
// the very row underneath it.

import type { SubSchedule } from "./schedule";
import { walkItems, getItemTotal, isClarification, calcItemCont } from "./calculations";

export interface ScheduleBand {
  /** ISO "YYYY-MM-DD", inclusive. */
  start: string;
  /** ISO "YYYY-MM-DD", exclusive — a band ending the day it starts is empty. */
  end: string;
  /** Share of the node's cost in this band, 0–100. Bands of an entry sum to 100. */
  pct: number;
}

export interface ScheduleEntry {
  bands: ScheduleBand[];
  /** 0–100, carried through from the single-band model. */
  pctComplete?: number;
}

/** Keyed by division id, subcategory id, or item id. */
export type CostSchedule = Record<string, ScheduleEntry>;

const MS_DAY = 86_400_000;
const MS_WEEK = 7 * MS_DAY;

export function fromISO(iso: string): Date {
  return new Date(iso + "T00:00:00");
}

export function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

/** A band is usable only if both dates parse and it covers at least one day. */
export function isValidBand(b: ScheduleBand | undefined | null): boolean {
  if (!b || !b.start || !b.end) return false;
  const s = fromISO(b.start).getTime();
  const e = fromISO(b.end).getTime();
  if (Number.isNaN(s) || Number.isNaN(e)) return false;
  return e > s;
}

/**
 * Drop unusable bands, sort by start, and rescale percentages to sum to 100.
 *
 * Rescaling rather than rejecting: percentages are edited by dragging a split
 * handle, so they drift. The invariant callers depend on is that an entry
 * accounts for all of its node's cost, not that the user typed round numbers.
 */
export function normalizeBands(bands: ScheduleBand[]): ScheduleBand[] {
  const valid = bands
    .filter(isValidBand)
    .map(b => ({ ...b, pct: Number.isFinite(b.pct) && b.pct > 0 ? b.pct : 0 }))
    .sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
  if (valid.length === 0) return [];
  const sum = valid.reduce((s, b) => s + b.pct, 0);
  if (sum <= 0) {
    // No usable weights — split the cost evenly rather than losing it.
    const even = 100 / valid.length;
    return valid.map(b => ({ ...b, pct: even }));
  }
  return valid.map(b => ({ ...b, pct: (b.pct / sum) * 100 }));
}

/** Earliest start and latest end across an entry's bands — the summary bar. */
export function entrySpan(entry: ScheduleEntry | null | undefined): { start: string; end: string } | null {
  const bands = (entry?.bands ?? []).filter(isValidBand);
  if (bands.length === 0) return null;
  let start = bands[0].start, end = bands[0].end;
  for (const b of bands) {
    if (b.start < start) start = b.start;
    if (b.end > end) end = b.end;
  }
  return { start, end };
}

/**
 * The most specific schedule wins. `chain` runs specific → general, e.g.
 * [itemId, subId, divId]. Entries with no usable band are skipped, so a
 * half-deleted entry falls through to the parent rather than zeroing the node.
 */
export function resolveEntry(chain: (string | null | undefined)[], schedule: CostSchedule): ScheduleEntry | null {
  for (const key of chain) {
    if (!key) continue;
    const entry = schedule[key];
    if (entry && entry.bands?.some(isValidBand)) return entry;
  }
  return null;
}

/** Split a band at a date, apportioning its percentage by duration. */
export function splitBand(band: ScheduleBand, atISO: string): [ScheduleBand, ScheduleBand] | null {
  if (!isValidBand(band)) return null;
  const s = fromISO(band.start).getTime();
  const e = fromISO(band.end).getTime();
  const at = fromISO(atISO).getTime();
  if (Number.isNaN(at) || at <= s || at >= e) return null;
  const share = (at - s) / (e - s);
  return [
    { start: band.start, end: atISO, pct: band.pct * share },
    { start: atISO, end: band.end, pct: band.pct * (1 - share) },
  ];
}

/**
 * Merge bands that have been dragged back together, so a split can be undone
 * by hand. Two bands merge when the gap between them is under `gapDays`;
 * their percentages add.
 */
export function mergeAdjacentBands(bands: ScheduleBand[], gapDays = 7): ScheduleBand[] {
  const sorted = normalizeBands(bands);
  if (sorted.length <= 1) return sorted;
  const out: ScheduleBand[] = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i++) {
    const prev = out[out.length - 1];
    const cur = sorted[i];
    const gap = (fromISO(cur.start).getTime() - fromISO(prev.end).getTime()) / MS_DAY;
    if (gap <= gapDays) {
      prev.end = cur.end > prev.end ? cur.end : prev.end;
      prev.pct += cur.pct;
    } else {
      out.push({ ...cur });
    }
  }
  return out;
}

/**
 * Spread a node's cost over a weekly grid.
 *
 * Returns an array of `totalWeeks` numbers, week 0 starting at `windowStart`.
 * Within a band the spread is flat; between bands it is zero, which is the
 * whole point of bands.
 *
 * Cost is conserved. A band that lands outside the window, or is too short to
 * cover a whole week, is clamped into the nearest week rather than dropped —
 * the previous implementation returned early on `dur <= 0` and silently lost
 * that money, so the curve could total less than the estimate for reasons
 * that had nothing to do with markup.
 */
export function spreadCost(
  cost: number,
  entry: ScheduleEntry | null | undefined,
  windowStart: Date,
  totalWeeks: number,
): number[] {
  const weeks = Array(Math.max(totalWeeks, 0)).fill(0) as number[];
  if (!cost || weeks.length === 0) return weeks;
  const bands = normalizeBands(entry?.bands ?? []);
  if (bands.length === 0) return weeks;

  const originMs = windowStart.getTime();
  const lastWeek = weeks.length - 1;

  for (const band of bands) {
    const amount = cost * (band.pct / 100);
    if (!amount) continue;
    const rawStart = Math.round((fromISO(band.start).getTime() - originMs) / MS_WEEK);
    const rawEnd = Math.round((fromISO(band.end).getTime() - originMs) / MS_WEEK);

    let sw = Math.max(0, Math.min(rawStart, lastWeek));
    let ew = Math.max(0, Math.min(rawEnd, weeks.length));
    if (ew <= sw) {
      // Shorter than a week, or clipped to nothing by the window edge: put it
      // in one week rather than losing it.
      sw = Math.max(0, Math.min(sw, lastWeek));
      ew = sw + 1;
    }
    const per = amount / (ew - sw);
    for (let w = sw; w < ew; w++) weeks[w] += per;
  }
  return weeks;
}

/** Total of an entry's bands as a share of cost — 100% for any usable entry. */
export function scheduledFraction(entry: ScheduleEntry | null | undefined): number {
  return normalizeBands(entry?.bands ?? []).length > 0 ? 1 : 0;
}

/**
 * A costed leaf of the estimate, with the chain that resolves its schedule.
 * `chain` runs specific to general, e.g. [itemId, subId, divId].
 */
export interface CostLeaf {
  id: string;
  cost: number;
  chain: string[];
}

/**
 * Weekly spend for a set of leaves, each resolved on its own.
 *
 * This is what makes scheduling a line item mean anything. Summing at the
 * subcategory level instead would read only the subcategory's entry, so an
 * item moved to a different month would keep being billed in the old one.
 */
export function spreadLeaves(
  leaves: CostLeaf[],
  schedule: CostSchedule,
  windowStart: Date,
  totalWeeks: number,
): number[] {
  const weeks = Array(Math.max(totalWeeks, 0)).fill(0) as number[];
  for (const leaf of leaves) {
    const entry = resolveEntry(leaf.chain, schedule);
    if (!entry) continue;
    const each = spreadCost(leaf.cost, entry, windowStart, totalWeeks);
    for (let w = 0; w < weeks.length; w++) weeks[w] += each[w];
  }
  return weeks;
}

/**
 * The span a parent bar should show: earliest start to latest end across what
 * is actually beneath it. A parent drawn from its own entry would contradict
 * its children the moment one of them was scheduled outside that window.
 */
export function rollupSpan(
  leaves: CostLeaf[],
  schedule: CostSchedule,
): { start: string; end: string } | null {
  let start: string | null = null, end: string | null = null;
  for (const leaf of leaves) {
    const span = entrySpan(resolveEntry(leaf.chain, schedule));
    if (!span) continue;
    if (start === null || span.start < start) start = span.start;
    if (end === null || span.end > end) end = span.end;
  }
  return start !== null && end !== null ? { start, end } : null;
}

/** True when any leaf carries a schedule of its own rather than inheriting. */
export function hasOwnLeafSchedule(leaves: CostLeaf[], schedule: CostSchedule): boolean {
  return leaves.some(l => schedule[l.id]?.bands?.some(isValidBand));
}

/**
 * The costed line items of one subcategory, each with its resolution chain.
 *
 * Uses the same inclusion rules as computeSubTotals, so the leaves of a
 * subcategory always sum to its subtotal. That is what lets the leaf-level
 * spread stay identical to the old subcategory-level one until somebody
 * actually schedules an item separately.
 */
export function leavesOfSubcategory(sub: any, divId: string, itemState: Record<string, any>): CostLeaf[] {
  const out: CostLeaf[] = [];
  for (const row of walkItems(sub.rows ?? [])) {
    const li = itemState[row.id];
    if (!li || !li.included) continue;
    if (li.altType && !(li.altType === "Alt" && li.altStatus === "Accepted")) continue;
    if (isClarification(li.costStatus ?? "")) continue;
    const cost = getItemTotal(li, itemState);
    if (cost === 0) continue;
    out.push({ id: row.id, cost, chain: [row.id, sub.id, divId] });
  }
  return out;
}

/** Every costed leaf in the estimate, keyed by subcategory id. */
export function buildCostLeaves(divisions: any[], itemState: Record<string, any>): Record<string, CostLeaf[]> {
  const out: Record<string, CostLeaf[]> = {};
  for (const div of divisions ?? []) {
    for (const sub of div.subcategories ?? []) {
      out[sub.id] = leavesOfSubcategory(sub, div.id, itemState);
    }
  }
  return out;
}

/** The markup inputs computeFinal uses, so a leaf can be loaded the same way. */
export interface MarkupInputs {
  overhead: number;
  profit: number;
  contingency: number;
  markupMode: string;
  divisionMarkups: Record<string, { overhead?: number | null; profit?: number | null }>;
}

/**
 * Load each leaf with its share of overhead, profit and contingency, so the
 * cash flow curve totals to the contract value rather than to direct cost.
 *
 * Mirrors computeFinal item for item: markup applies unless the item is
 * flagged excludeFromMarkup, at the division's own rate when any division
 * overrides exist and the project rate otherwise, shaped by markupMode.
 * Contingency comes from calcItemCont, which already knows about per-item
 * modes. Spreading these with the work is the conventional assumption --
 * overhead and profit are billed as the work is, and contingency is drawn
 * roughly as risk materialises -- and it is what makes the curve usable
 * with an owner or a lender, who care about the contract total.
 */
export function loadLeaves(
  leaves: CostLeaf[],
  itemState: Record<string, any>,
  divIdOf: (leaf: CostLeaf) => string,
  m: MarkupInputs,
): CostLeaf[] {
  const hasCustom = Object.values(m.divisionMarkups ?? {}).some(
    d => d?.overhead != null || d?.profit != null);
  return leaves.map(leaf => {
    const li = itemState[leaf.id] ?? {};
    let loaded = leaf.cost;
    if (!li.excludeFromMarkup) {
      const dm = hasCustom ? (m.divisionMarkups[divIdOf(leaf)] ?? {}) : {};
      const oh = dm.overhead != null ? dm.overhead : m.overhead;
      const pr = dm.profit != null ? dm.profit : m.profit;
      if (m.markupMode !== "profit") loaded += leaf.cost * (oh / 100);
      if (m.markupMode !== "overhead" && m.markupMode !== "combined") loaded += leaf.cost * (pr / 100);
    }
    loaded += calcItemCont(li, m.contingency, itemState);
    return { ...leaf, cost: loaded };
  });
}

/**
 * Convert the single-window-per-subcategory model to bands. Lossless: each
 * subcategory becomes one entry with one band carrying all of its cost.
 */
export function migrateSubSchedule(old: SubSchedule | null | undefined): CostSchedule {
  const out: CostSchedule = {};
  for (const [id, e] of Object.entries(old ?? {})) {
    if (!e?.startDate || !e?.endDate) continue;
    const band: ScheduleBand = { start: e.startDate, end: e.endDate, pct: 100 };
    if (!isValidBand(band)) {
      // A same-day entry is legal in the old model; give it a one-day window
      // so it still carries cost rather than being dropped on migration.
      band.end = toISO(addDays(fromISO(e.startDate), 1));
      if (!isValidBand(band)) continue;
    }
    out[id] = { bands: [band], pctComplete: e.pctComplete ?? 0 };
  }
  return out;
}

/** Back-convert for anything still reading the single-window shape. */
export function toSubSchedule(schedule: CostSchedule): SubSchedule {
  const out: SubSchedule = {};
  for (const [id, entry] of Object.entries(schedule)) {
    const span = entrySpan(entry);
    if (!span) continue;
    out[id] = { startDate: span.start, endDate: span.end, pctComplete: entry.pctComplete ?? 0 };
  }
  return out;
}
